
import React, { Suspense, useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AnalyticsPageViews } from './components/AnalyticsPageViews';
import { SWRConfig } from 'swr';
import { HelmetProvider } from 'react-helmet-async';
import { VenetianMask, X } from 'lucide-react';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GlobalLoader } from './components/GlobalLoader';
import { LandingSkeleton, StoreSkeleton } from './components/ui/Skeleton';
import { CookieConsent } from './components/ui/CookieConsent';
import { ErrorBoundary } from './components/ErrorBoundary';

// Critical Pages (Instant Load)
import { PublicLanding } from './components/PublicLanding';
import { StoreFront } from './components/StoreFront';

// Routes
import { ProtectedRoute } from './routes/ProtectedRoute';
import { StoreRoute } from './routes/StoreRoute';

// Lazy load all authenticated/dashboard pages
const Dashboard = React.lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const CustomerOrders = React.lazy(() => import('./pages/CustomerOrders').then(module => ({ default: module.CustomerOrders })));
const InfoPage = React.lazy(() => import('./pages/InfoPage').then(module => ({ default: module.InfoPage })));
const WithdrawPage = React.lazy(() => import('./pages/WithdrawPage').then(module => ({ default: module.WithdrawPage })));
const UnsubscribePage = React.lazy(() => import('./pages/UnsubscribePage').then(module => ({ default: module.UnsubscribePage })));
const UpdatePassword = React.lazy(() => import('./pages/UpdatePassword').then(module => ({ default: module.UpdatePassword })));
const GuidePage = React.lazy(() => import('./pages/GuidePage').then(module => ({ default: module.GuidePage })));

// Smart Fallback Component
const SmartFallback = () => {
    const [hash, setHash] = useState(window.location.hash);

    useEffect(() => {
        const onHashChange = () => setHash(window.location.hash);
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    const path = hash.split('?')[0];

    if (path === '#/' || path === '' || path === '#') return <LandingSkeleton />;
    if (path.startsWith('#/store') || path.startsWith('#/p/')) return <StoreSkeleton />;

    return <GlobalLoader message="Laddar..." />;
};

// Main App Layout that uses the AuthContext
const AppLayout = () => {
    const { user, impersonatingAdmin, handleLogout } = useAuth();

    return (
        <ToastProvider>
        <ConfirmProvider>
            <HashRouter>
                <AnalyticsPageViews />
                <CookieConsent />
                
                {impersonatingAdmin && user && (
                    // Discrete Spy Mode Header
                    <div className="bg-stone-900 text-stone-400 px-4 py-2 flex justify-between items-center text-[10px] font-bold uppercase tracking-widest sticky top-0 z-[100] border-b border-stone-800">
                        <div className="flex items-center gap-2">
                            <VenetianMask size={14} className="text-orange-400" />
                            <span>Simulerar vy som: <strong className="text-white ml-1 border-b border-stone-700 pb-0.5">{user.name}</strong></span>
                        </div>
                        <button 
                            onClick={handleLogout} 
                            className="flex items-center gap-2 hover:text-white transition-colors bg-stone-800 hover:bg-stone-700 px-3 py-1 rounded-lg"
                        >
                            Avsluta <X size={12} />
                        </button>
                    </div>
                )}
                
                <ErrorBoundary>
                <Suspense fallback={<SmartFallback />}>
                    <Routes>
                        <Route path="/" element={<PublicLanding />} />
                        <Route path="/store" element={<StoreFront />} />
                        <Route path="/p/:slug" element={<StoreRoute />} />
                        <Route path="/my-orders" element={<CustomerOrders />} />
                        {/* Ångerfunktionen (lagen om distansavtal): alltid nåbar, länkad i sidfot och mejl. */}
                        <Route path="/angra" element={<WithdrawPage />} />
                        {/* Guiderna för ansvariga och säljare: öppna, länkas från onboarding-mejlet. */}
                        <Route path="/guide/:who" element={<GuidePage />} />
                        
                        {/* Specific Info Routes */}
                        <Route path="/info/unsubscribe" element={<UnsubscribePage />} />
                        
                        {/* Generic Info Routes (Must come after specific ones) */}
                        <Route path="/info/:page" element={<InfoPage />} />
                        
                        <Route path="/login" element={<Navigate to="/" replace />} />
                        
                        {/* Auth Required Routes */}
                        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                        
                        {/* Public but handles its own security check to allow for Magic Link race conditions */}
                        <Route path="/update-password" element={<UpdatePassword />} />
                        
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </Suspense>
                </ErrorBoundary>
            </HashRouter>
        </ConfirmProvider>
        </ToastProvider>
    );
};

const App = () => {
  return (
    <HelmetProvider>
        <SWRConfig value={{
            revalidateOnFocus: true, 
            revalidateIfStale: true, 
            refreshInterval: 10000, 
            dedupingInterval: 5000, 
            shouldRetryOnError: true,
            errorRetryInterval: 5000,
            errorRetryCount: 3
        }}>
            <AuthProvider>
                <AppLayout />
            </AuthProvider>
        </SWRConfig>
    </HelmetProvider>
  );
};

export default App;
